import { useEnvStore } from '@/stores/envStore';
import type { DaysOffResponse } from '@/types/api';
import type { DuplicateWeekResponse } from '@/types/dayOffValidation';
import type { WorkingPeriod } from '@/types/employee';
import { formatLocalDate, toUTCString } from '@/utils/time';
import { ref } from 'vue';
import { useFetch } from './useFetch';

export function useEmployee() {
  const { fetchData } = useFetch();
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const envStore = useEnvStore();
  const apiUrl = envStore.apiUrl;

  async function apiCall<T>(
    url: string,
    method: 'GET' | 'POST' | 'DELETE',
    payload?: Record<string, unknown> | FormData,
  ): Promise<{ data: T | null; error: unknown }> {
    isLoading.value = true;
    error.value = null;
    try {
      const { data, error: fetchErr } = await fetchData(url, method, payload);
      isLoading.value = false;
      if (fetchErr) return { data: null, error: fetchErr };
      return { data: data as T, error: null };
    } catch (err) {
      isLoading.value = false;
      const msg = err instanceof Error ? err.message : 'An error occurred';
      error.value = msg;
      return { data: null, error: msg };
    }
  }

  /**
   * Start a new working period for the employee
   * @param restaurant_user_id The ID of the employee
   * @returns Object containing the created working period data or error
   */
  async function startWorkingPeriod(restaurant_user_id: number, options?: { start_time?: Date | string }) {
    isLoading.value = true;
    error.value = null;

    try {
      const payload: { restaurant_user_id: number; start_time?: string } = { restaurant_user_id };

      if (options && options.start_time) {
        payload.start_time = toUTCString(options.start_time);
      }

      const { data, error: fetchError } = await fetchData(`${envStore.apiUrl}/working-periods/${restaurant_user_id}/start`, 'POST', payload);

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }

      return {
        data: data?.working_period || data,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';

      return {
        data: null,
        error: error.value,
      };
    }
  }

  /**
   * End a working period for the employee
   * @param restaurant_user_id The ID of the employee
   * @param options Additional options like end_time or last_period_of_day
   * @returns Object containing the ended working period data or error
   */
  const endWorkingPeriod = async (restaurant_user_id: number, options?: { end_time?: string; last_period_of_day?: boolean }) => {
    isLoading.value = true;
    error.value = null;

    try {
      // Build the payload with restaurant_user_id
      const payload: { restaurant_user_id: number; end_time?: string; last_period_of_day?: boolean } = { restaurant_user_id };

      // If there's an end_time, convert it to UTC
      if (options?.end_time) {
        payload.end_time = toUTCString(options.end_time);
      }

      // Add any additional options
      if (options?.last_period_of_day !== undefined) {
        payload.last_period_of_day = options.last_period_of_day;
      }

      const { data, error: fetchError } = await fetchData(`${envStore.apiUrl}/working-periods/${restaurant_user_id}/end`, 'POST', payload);

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }

      return {
        data: data?.working_period || data,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';

      return {
        data: null,
        error: error.value,
      };
    }
  };

  /**
   * Get working periods for an employee
   * @param restaurant_user_id The ID of the employee
   * @param options Optional date filters
   * @returns working_periods : WorkingPeriods
   */
  const getWorkingPeriods = async (
    restaurant_user_id: number,
    options?: { start_date?: string; end_date?: string },
  ): Promise<{ data: { working_periods: WorkingPeriod[] } | null; error: unknown }> => {
    isLoading.value = true;
    error.value = null;

    try {
      let url = `${envStore.apiUrl}/working-periods/${restaurant_user_id}`;

      // Add query parameters if provided
      if (options) {
        const params = new URLSearchParams();
        if (options.start_date) params.append('start_date', formatLocalDate(new Date(options.start_date)));
        if (options.end_date) params.append('end_date', formatLocalDate(new Date(options.end_date)));

        if (params.toString()) {
          url += `?${params.toString()}`;
        }
      }

      const { data, error: fetchError } = await fetchData(url, 'GET');

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }

      return {
        data,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';

      return {
        data: null,
        error: error.value,
      };
    }
  };

  /**
   * Get paginated working periods for an employee
   * @param restaurant_user_id The ID of the employee
   * @param options Pagination and filter options
   */
  const getPaginatedWorkingPeriods = async (
    restaurant_user_id: number,
    options?: {
      page?: number;
      per_page?: number;
      start_date?: string;
      end_date?: string;
      mode?: 'days' | 'records';
      before_date?: string;
    },
  ): Promise<{
    data: {
      working_periods: WorkingPeriod[];
      pagination: {
        current_page?: number;
        per_page: number;
        total?: number;
        last_page?: number;
        has_more_pages: boolean;
        from?: number | null;
        to?: number | null;
        next_cursor?: string | null;
      };
      date_bounds?: {
        earliest_date: string | null;
        latest_date: string | null;
        has_entries: boolean;
      };
    } | null;
    error: unknown;
  }> => {
    isLoading.value = true;
    error.value = null;

    try {
      let url = `${envStore.apiUrl}/working-periods/${restaurant_user_id}/paginated`;

      // Add query parameters if provided
      if (options) {
        const params = new URLSearchParams();
        if (options.page) params.append('page', options.page.toString());
        if (options.per_page) params.append('per_page', options.per_page.toString());
        if (options.start_date) params.append('start_date', formatLocalDate(new Date(options.start_date)));
        if (options.end_date) params.append('end_date', formatLocalDate(new Date(options.end_date)));
        if (options.mode) params.append('mode', options.mode);
        if (options.before_date) params.append('before_date', options.before_date);

        if (params.toString()) {
          url += `?${params.toString()}`;
        }
      }

      const { data, error: fetchError } = await fetchData(url, 'GET');

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }

      return {
        data,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';

      return {
        data: null,
        error: error.value,
      };
    }
  };

  /**
   *
   * @param restaurant_user_id restaurant_user id - connection between the user and the restaurant
   * @returns balance of the users (how many hours they worked)
   */
  async function getBalance(restaurant_user_id: number) {
    isLoading.value = true;
    error.value = null;

    try {
      const { data, error: fetchError } = await fetchData(`${envStore.apiUrl}/working-periods/${restaurant_user_id}/balance`, 'GET');

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }

      return {
        data,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';

      return {
        data: null,
        error: error.value,
      };
    }
  }

  async function getLastWorkingPeriod(restaurant_user_id: number) {
    isLoading.value = true;
    error.value = null;

    try {
      const { data, error: fetchError } = await fetchData(`${envStore.apiUrl}/working-periods/${restaurant_user_id}/last`, 'GET');

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }

      return {
        data,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';

      return {
        data: null,
        error: error.value,
      };
    }
  }

  /**
   * Duplicate a week's shifts to other weeks
   * @param params Object containing restaurant_id, source_week_start, target_week_starts
   * @returns Promise with result or error
   */
  async function duplicateWeek(params: { restaurant_id: string; source_week_start: string; target_week_starts: string[] }) {
    isLoading.value = true;
    error.value = null;

    try {
      const { data, error: fetchError } = await fetchData(`${envStore.apiUrl}/planning/employees/duplicate-week`, 'POST', params);

      isLoading.value = false;

      if (fetchError) {
        return { data: null, error: fetchError };
      }

      // The response now includes successful and failed assignments
      const response = data as DuplicateWeekResponse;
      return { data: response, error: null };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';
      return { data: null, error: error.value };
    }
  }

  async function fetchUserDaysOff(restaurantId: string, userId: string): Promise<DaysOffResponse | null> {
    if (!restaurantId || !userId) {
      console.error('Restaurant ID and User ID are required to fetch days off.');
      return null;
    }
    const { data, error } = await fetchData(`${apiUrl}/manager/${restaurantId}/team/${userId}/days-off`, 'GET');
    if (error) {
      console.error('Error fetching user days off:', error);
      return null;
    }
    return data as DaysOffResponse;
  }

  /**
   * Add days off for a user
   * @param restaurantId ID of the restaurant
   * @param userId ID of the user
   * @param dates Array of date strings formatted YYYY-MM-DD
   * @returns the created days off response or error
   */
  async function addDaysOff(restaurantId: string, userId: string, dates: string[]) {
    return apiCall<DaysOffResponse>(`${envStore.apiUrl}/manager/${restaurantId}/team/${userId}/days-off`, 'POST', { dates });
  }

  /**
   * Delete days off for a user
   * @param restaurantId ID of the restaurant
   * @param userId ID of the user
   * @param dates Array of date strings formatted YYYY-MM-DD to delete
   * @returns the deletion response or error
   */
  async function deleteDaysOff(restaurantId: string, userId: string, dates: string[]): Promise<{ data: DaysOffResponse | null; error: unknown }> {
    return apiCall<DaysOffResponse>(`${envStore.apiUrl}/manager/${restaurantId}/team/${userId}/days-off`, 'DELETE', { dates });
  }

  /**
   * Create a manual working period
   * @param restaurant_user_id The ID of the employee
   * @param data Working period data
   */
  const createManualWorkingPeriod = async (
    restaurant_user_id: number,
    data: {
      start_time: string;
      end_time?: string;
      last_period_of_day?: boolean;
    },
  ): Promise<{ data: WorkingPeriod | null; error: unknown }> => {
    isLoading.value = true;
    error.value = null;

    try {
      const { data: response, error: fetchError } = await fetchData(`${envStore.apiUrl}/working-periods/${restaurant_user_id}/manual`, 'POST', data);

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }

      return {
        data: response?.working_period || response,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';

      return {
        data: null,
        error: error.value,
      };
    }
  };

  /**
   * Update a working period
   * @param restaurant_user_id The ID of the employee
   * @param working_period_id The ID of the working period
   * @param data Updated working period data
   */
  const updateWorkingPeriod = async (
    restaurant_user_id: number,
    working_period_id: number,
    data: {
      start_time?: string;
      end_time?: string;
      last_period_of_day?: boolean;
    },
  ): Promise<{ data: WorkingPeriod | null; error: unknown }> => {
    isLoading.value = true;
    error.value = null;

    try {
      const { data: response, error: fetchError } = await fetchData(
        `${envStore.apiUrl}/working-periods/${restaurant_user_id}/${working_period_id}`,
        'PUT',
        data,
      );

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }

      return {
        data: response?.working_period || response,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';

      return {
        data: null,
        error: error.value,
      };
    }
  };

  /**
   * Delete a working period
   * @param restaurant_user_id The ID of the employee
   * @param working_period_id The ID of the working period
   */
  const deleteWorkingPeriod = async (restaurant_user_id: number, working_period_id: number): Promise<{ data: unknown | null; error: unknown }> => {
    isLoading.value = true;
    error.value = null;

    try {
      const { data: response, error: fetchError } = await fetchData(
        `${envStore.apiUrl}/working-periods/${restaurant_user_id}/${working_period_id}`,
        'DELETE',
      );

      isLoading.value = false;

      if (fetchError) {
        return {
          data: null,
          error: fetchError,
        };
      }

      return {
        data: response,
        error: null,
      };
    } catch (err: unknown) {
      isLoading.value = false;
      error.value = err instanceof Error && err.message ? err.message : 'An error occurred';

      return {
        data: null,
        error: error.value,
      };
    }
  };

  /**
   * Get absences for a specific week range
   * @param restaurantId The restaurant ID
   * @param startDate Start date of the week (YYYY-MM-DD)
   * @param endDate End date of the week (YYYY-MM-DD)
   * @returns Object containing absences data or error
   */
  const getAbsencesForWeek = async (
    restaurantId: string,
    startDate: string,
    endDate: string,
  ): Promise<{ data: { absences: Array<{ employee_name: string; date_ranges: string[]; total_days: number }> } | null; error: unknown }> => {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });

    const url = `${apiUrl}/manager/${restaurantId}/absences/week?${params.toString()}`;

    return apiCall<{ absences: Array<{ employee_name: string; date_ranges: string[]; total_days: number }> }>(url, 'GET');
  };

  return {
    isLoading,
    error,
    startWorkingPeriod,
    endWorkingPeriod,
    getWorkingPeriods,
    getPaginatedWorkingPeriods,
    getBalance,
    getLastWorkingPeriod,
    createManualWorkingPeriod,
    updateWorkingPeriod,
    deleteWorkingPeriod,
    duplicateWeek,
    fetchUserDaysOff,
    addDaysOff,
    deleteDaysOff,
    getAbsencesForWeek,
  };
}
